<script>
import { mapGetters } from 'vuex';
import mixin from '@/plans/mx/shopping-cart';
import {
  isZoomMeetingAsAddonCard,
  ZOOM_METTING_EDU_I18N_TITLE_KEY,
  ZOOM_MEETING_SUB_TYPES,
} from '@/plans/ZOOM_MEETING/common/utils';
import { CART_DISPLAY_MODE } from '../mx/shopping-cart.vue';
import { getAgreementAcceptedDesc } from './config/healthcare/helpers';
import AltProratedPlanUser from '@/views/config/shopping-cart/prorated/plan-user.vue';
import AltProratedPlanPrice from '@/views/config/shopping-cart/prorated/plan-price.vue';
import AltProratedPlanPeriod from '@/views/config/shopping-cart/prorated/plan-period.vue';
import { BILLING_CYCLE } from '@/common/constants';
import { isFromShoppingCart } from '@/views/config/shopping-cart/persist/plan-remove';
import {
  checkIsShowInrDiscount,
  getInrProAnnualCustomPriceConfig,
} from '@/views/config/shopping-cart/inr-discount/inr-discount';

export const categories = {
  basic: 'billing.planform.title.zoom_one',
  pro: 'billing.planform.title.zoom_one_pro',
  pro_single_use: 'billing.planform.title.zoom_one_pro',
  biz: 'billing.planform.title.zoom_one_biz',
  education: ZOOM_METTING_EDU_I18N_TITLE_KEY,
};

export default {
  mixins: [mixin],
  components: {
    AltProratedPlanUser,
    AltProratedPlanPrice,
    AltProratedPlanPeriod,
  },
  computed: {
    ...mapGetters(['healthcareInfo', 'addressInfo']),
    mode() {
      if (this.$context.is.delta) return CART_DISPLAY_MODE.DELTA;
      return CART_DISPLAY_MODE.SUM;
    },
    shouldRenderHealthcareCart({ healthcareInfo, addressInfo }) {
      return (
        healthcareInfo.agreementAccepted &&
        // On the checkout page, users can opt out of healthcare by selecting a country that doesn't meet the conditions.
        !(this.$context.is.checkoutPage && addressInfo.country && healthcareInfo.countryCode !== addressInfo.country)
      );
    },
    showRemove() {
      // Hack: plan remove logic places in mx_remove when the user is from shopping bag
      if (isFromShoppingCart()) {
        return true;
      }
      return !this.mx_isLastBasePlanForTheSameBillingCycle && (!this.mx_isLastBasePlan || isZoomMeetingAsAddonCard());
    },
  },
  methods: {
    renderTitle(plan, value) {
      if ((this.$context.is.pro2biz || this.migratePlanState.is.pro2biz) && !this.$context.is.edu) {
        return (
          <div>
            {this.mx_title(this.$t(categories['basic']), undefined, plan?.plan_code, plan?.parent_category)}
            {this.mx_subTitle(
              this.$t('billing.pro2biz.title'),
              <AltProratedPlanPrice type={plan.type}>{this.$money(this.mx_price$(plan))}</AltProratedPlanPrice>,
              plan?.plan_code,
            )}
          </div>
        );
      }
      if (value) {
        return (
          <div>
            {this.mx_main_title(this.$t(categories[plan.category] || plan.planNameKey), value, plan?.parent_category)}
          </div>
        );
      }
      return (
        <div>
          {this.mx_title(
            this.$t(categories[plan.category] || plan.planNameKey),
            <AltProratedPlanPrice type={plan.type}>{this.$money(this.mx_price$(plan))}</AltProratedPlanPrice>,
            plan?.plan_code,
            undefined,
            plan.category === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP ? ['text-no-capitalize'] : null
          )}
        </div>
      );
    },

    renderHealthcareCart() {
      return (
        <div class="opc-healthcare-cart">
          {this.mx_subTitle(this.$t('billing.healthcare.title'))}
          <div class="opc-cart-item__text">
            {getAgreementAcceptedDesc(this.healthcareInfo.businessCountry, this.$t.bind(this))}
          </div>
        </div>
      );
    },

    $renderSuccessTitle() {
      return this.mx_plans.map((plan) => {
        return <div class="opc-cart-item">{this.renderTitle(plan, this.titleIncluded(plan))}</div>;
      });
    },

    renderInrDiscount(plan) {
      const { priceChangeRatio } = getInrProAnnualCustomPriceConfig();
      if (checkIsShowInrDiscount(plan)) {
        return (
          <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__">
            <span>{this.$t('billing.coupon.discount')}</span>
            <span class="opc-cart-item__value text-no-capitalize">
              {this.$money((this.mx_price$(plan) * -priceChangeRatio) / 100)}
            </span>
          </div>
        );
      }

      return null;
    },
    mx_license(num, unit = this.$t('billing.planform.unit.user')) {
      if (this.$context.is.modalDelta) {
        unit = this.$t('billing.planform.unit.license')
      }

      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    $renderPrice(plans) {
      return (plans || this.mx_plans).map((plan) => {
        {
          /* FREE TRIAL - Specific UI for free trial */
        }
        if (!!plan.isOnlineFreeTrial && !!plan.trialDays) {
          return (
            <div class="opc-cart-item">
              {this.mx_freeTrialTitle(this.$t(categories[plan.category] || plan.planNameKey), plan?.plan_code)}
              {this.mx_license(plan.cartInfo.selected_number)}
              {this.mx_freeTrialDesc(plan.trialDays)}
              {this.mx_discount(plan.plan_code)}
              {this.mx_cycle(plan.billing_cycle)}
              {this.mx_edit(plan)}
            </div>
          );
        }
        return (
          <div class="opc-cart-item">
            {this.renderTitle(plan, this.titleIncluded(plan))}
            {this.mx_discount(plan.plan_code)}
            {this.renderInrDiscount(plan)}
            <AltProratedPlanUser type={plan.type}>{this.mx_license$(plan)}</AltProratedPlanUser>
            {this.mx_expire_date(this.expiredDate(plan)) || this.mx_cycle(plan.billing_cycle)}
            <AltProratedPlanPeriod type={plan.type} />
            {plan.billing_cycle === BILLING_CYCLE.ONE_TIME ? '' : this.mx_edit(plan)}
            {this.showRemove && this.mx_remove(plan)}
            {this.shouldRenderHealthcareCart && this.renderHealthcareCart()}
          </div>
        );
      });
    },
    expiredDate(plan) {
      if (plan.billing_cycle === BILLING_CYCLE.ONE_TIME) {
        return this.$t('billing.single_use_meeting.expire_desc_for_pro');
      }
      return '';
    },
    titleIncluded(plan) {
      if (plan.billing_cycle === BILLING_CYCLE.ONE_TIME) {
        return this.$t('billing.event_services.price_included');
      }
      return undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
.opc-healthcare-cart {
  margin-top: 8px;
}
</style>
